import { useNotification } from './useNotification';
import { useI18n } from 'vue-i18n';
import type { AxiosError } from 'axios';

export interface ApiErrorResponse {
  timestamp?: string;
  status?: number;
  message?: string;
  path?: string;
  details?: string | Record<string, unknown>;
  error?: string;
}

export interface ApiError {
  response?: {
    status?: number;
    data?: ApiErrorResponse;
  };
  message?: string;
  code?: string;
}

const FALLBACK_MESSAGES: Record<string, string> = {
  'api.errors.badRequest': 'The request was invalid. Please check your input and try again.',
  'api.errors.unauthorized': 'You are not authorized. Please log in.',
  'api.errors.forbidden': "You don't have permission to access this resource.",
  'api.errors.notFound': 'The requested resource was not found.',
  'api.errors.conflict': 'This resource conflicts with existing data.',
  'api.errors.validationError': 'Please check your input and try again.',
  'api.errors.tooManyRequests': 'Too many requests. Please wait and try again.',
  'api.errors.internalServerError': 'An internal server error occurred. Please try again later.',
  'api.errors.badGateway': 'The server is temporarily unavailable. Please try again later.',
  'api.errors.serviceUnavailable':
    'The service is temporarily unavailable. Please try again later.',
  'api.errors.gatewayTimeout': 'The request timed out. Please try again.',
  'api.errors.clientError': 'A client error occurred. Please check your request.',
  'api.errors.serverError': 'A server error occurred. Please try again later.',
  'api.errors.generic': 'An unexpected error occurred. Please try again.',
};

export function useApiError() {
  const { showApiError } = useNotification();
  const { t } = useI18n();

  /** Only translate known api.errors.* keys; never pass backend/user text to t() to avoid ICU SyntaxError. */
  const safeT = (key: string): string => {
    if (!/^api\.errors\.[a-zA-Z]+$/.test(key)) return FALLBACK_MESSAGES[key] ?? key;
    try {
      const result = t(key);
      return typeof result === 'string' ? result : FALLBACK_MESSAGES[key] ?? key;
    } catch {
      return FALLBACK_MESSAGES[key] ?? key;
    }
  };

  const getErrorMessage = (error: unknown): string => {
    const apiError = error as ApiError;

    // Check if it's an API error with structured response
    if (apiError.response?.data?.message) {
      return apiError.response.data.message;
    }

    // Check for error field in response
    if (apiError.response?.data?.error) {
      return apiError.response.data.error;
    }

    // Check for details field
    if (apiError.response?.data?.details) {
      const details = apiError.response.data.details;
      if (typeof details === 'string') {
        return details;
      }
      // If details is an object, try to extract meaningful message
      if (typeof details === 'object' && details !== null) {
        const firstKey = Object.keys(details)[0];
        if (firstKey && typeof details[firstKey] === 'string') {
          return details[firstKey] as string;
        }
      }
    }

    // Check for generic error message
    if (apiError.message) {
      return apiError.message;
    }

    // Check for specific HTTP status codes
    const status = apiError.response?.status;
    if (status) {
      switch (status) {
        case 400:
          return safeT('api.errors.badRequest');
        case 401:
          return safeT('api.errors.unauthorized');
        case 403:
          return safeT('api.errors.forbidden');
        case 404:
          return safeT('api.errors.notFound');
        case 409:
          return safeT('api.errors.conflict');
        case 422:
          return safeT('api.errors.validationError');
        case 429:
          return safeT('api.errors.tooManyRequests');
        case 500:
          return safeT('api.errors.internalServerError');
        case 502:
          return safeT('api.errors.badGateway');
        case 503:
          return safeT('api.errors.serviceUnavailable');
        case 504:
          return safeT('api.errors.gatewayTimeout');
        default:
          if (status >= 400 && status < 500) {
            return safeT('api.errors.clientError');
          } else if (status >= 500) {
            return safeT('api.errors.serverError');
          }
      }
    }

    // Fallback to generic error message
    return safeT('api.errors.generic');
  };

  const handleApiError = (error: unknown, context?: string): string => {
    const errorMessage = getErrorMessage(error);

    // Log error for debugging (in development)
    if (import.meta.env.MODE === 'development') {
      console.error(`API Error${context ? ` in ${context}` : ''}:`, error);
    }

    // Show error notification
    showApiError(error, context);

    return errorMessage;
  };

  const isNetworkError = (error: unknown): boolean => {
    const apiError = error as AxiosError;
    return !apiError.response && apiError.request;
  };

  const isServerError = (error: unknown): boolean => {
    const apiError = error as ApiError;
    const status = apiError.response?.status;
    return status ? status >= 500 : false;
  };

  const isClientError = (error: unknown): boolean => {
    const apiError = error as ApiError;
    const status = apiError.response?.status;
    return status ? status >= 400 && status < 500 : false;
  };

  const isValidationError = (error: unknown): boolean => {
    const apiError = error as ApiError;
    return apiError.response?.status === 422;
  };

  const isConflictError = (error: unknown): boolean => {
    const apiError = error as ApiError;
    return apiError.response?.status === 409;
  };

  const isUnauthorizedError = (error: unknown): boolean => {
    const apiError = error as ApiError;
    return apiError.response?.status === 401;
  };

  return {
    handleApiError,
    getErrorMessage,
    isNetworkError,
    isServerError,
    isClientError,
    isValidationError,
    isConflictError,
    isUnauthorizedError,
  };
}
